import { useCallback } from "react";
import type { ChannelMetrics } from "@/hooks/api/use-channel-metrics";
import { buildFilterGroupFromColumnState } from "@/lib/filters";
import type { QueryParams } from "@/lib/process-query-params";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import type { Channel } from "./api/use-channels";
import { type BaseListFilters, useListFilters } from "./use-list-filters";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Default sort settings for channels
const DEFAULT_SORT_COLUMN = "name";
const DEFAULT_SORT_DIRECTION: SortDirection = "asc";

// SessionStorage keys
const CHANNEL_LIST_FILTERS_KEY = "channels::list::filters";
const CHANNEL_METRICS_KEY = "channels::list::metrics";

const DEFAULT_METRICS: Array<keyof ChannelMetrics> = [
  "accounts_engaged",
  "total_engagements",
  "people_engaged",
];

// Interface for filter configuration
interface ChannelListFilters extends BaseListFilters {
  sortColumn?: string;
  sortDirection?: SortDirection;
  search?: string;
}

// Interface for query parameters
interface ChannelsQueryParams extends QueryParams {
  search?: string;
  sort_by?: string;
  sort_direction?: SortDirection;
  null_sort?: "max" | "min" | "first" | "last" | undefined;
  limit?: number;
  metrics?: Array<keyof ChannelMetrics>;
  metric_filters?: Record<string, unknown>;
}

/**
 * Hook for managing channel metrics
 * @returns Methods for getting and setting selected metrics
 */
export function useChannelMetrics() {
  // Get and set metrics using sessionStorage for tab-specific behavior
  const [selectedMetrics, setSelectedMetrics] = useTrackedSessionStorage<
    Array<keyof ChannelMetrics>
  >(CHANNEL_METRICS_KEY, DEFAULT_METRICS);

  const getDefaultMetrics = useCallback(() => DEFAULT_METRICS, []);

  return {
    selectedMetrics,
    setSelectedMetrics,
    getDefaultMetrics,
  };
}

/**
 * Hook for managing channel list filters
 * @returns Filters and utilities for managing channel list filters
 */
export function useChannelListFilters() {
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams: getBaseQueryParams,
  } = useListFilters<
    ChannelListFilters,
    UnifiedTableColumn<Channel>,
    ChannelsQueryParams
  >({
    storageKey: CHANNEL_LIST_FILTERS_KEY,
    defaultSortColumn: DEFAULT_SORT_COLUMN,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    buildQueryParams,
  });

  // Helper function to build query params
  function buildQueryParams(
    filters: ChannelListFilters,
    options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<Channel>[];
      limit?: number;
    },
  ): ChannelsQueryParams {
    const { searchTerm, columns = [], limit = 100 } = options || {};

    // Sort handling
    let sortCol = filters.sortColumn || DEFAULT_SORT_COLUMN;

    // Fallback if sort column no longer exists in current column set
    const validSortKeys = columns.map((c) => c.key);
    if (!validSortKeys.includes(sortCol)) {
      sortCol = DEFAULT_SORT_COLUMN;
    }

    const sortDir = filters.sortDirection || DEFAULT_SORT_DIRECTION;

    // Find column definition to get null_sort behaviour
    const column = columns.find((col) => col.key === sortCol);

    const params: ChannelsQueryParams = {
      sort_by: sortCol,
      sort_direction: sortDir,
      null_sort: column?.nullSort,
      search: searchTerm || filters.search || undefined,
      limit,
    };

    // Structured filters JSON
    const validColumnKeys = new Set(columns.map((c) => c.key.toString()));
    const group = buildFilterGroupFromColumnState(filters, validColumnKeys);
    if (group) params.filters = JSON.stringify(group);

    return params;
  }

  // Wrapper for getQueryParams that accepts metrics as a parameter
  const getQueryParams = useCallback(
    (options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<Channel>[];
      limit?: number;
      metrics?: Array<keyof ChannelMetrics>;
      metric_filters?: Record<string, unknown>;
    }) => {
      const { metrics, metric_filters, ...restOptions } = options || {};

      // Get base query params from filters
      const params = getBaseQueryParams(restOptions) as ChannelsQueryParams & {
        metrics?: Array<keyof ChannelMetrics>;
        metric_filters?: Record<string, unknown>;
      };

      // Add metrics and metric_filters if provided
      if (metrics) {
        params.metrics = metrics;
      }

      if (metric_filters) {
        params.metric_filters = metric_filters;
      }

      return params;
    },
    [getBaseQueryParams],
  );

  const getDefaultMetrics = useCallback(() => DEFAULT_METRICS, []);

  return {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
    getDefaultMetrics,
  };
}
